import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { warmAuthSession } from './sessionWarm';

// How long the warm may run before we say anything. Under this the round-trip
// is imperceptible and an indicator would only blink; over it, rendering
// nothing reads as a blank, broken page -- which is precisely the slow-Cognito
// case this gate exists for. Two states rather than one, so neither the fast
// path nor the slow path is served badly.
export const WARMING_NOTICE_DELAY_MS = 250;

// The spinner is decorative -- the text beside it is the announcement, so it
// must not carry a label of its own (StatusMessage's idiom, same visual).
const WarmingNotice = () => (
  <p className="text-center text-body-secondary mt-4" role="status" aria-live="polite">
    <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
    <span className="p-1">Signing you in…</span>
  </p>
);

// Holds the /me/* page tree back until the auth session has been warmed once
// (see sessionWarm.js for why). Mounted INSIDE the Authenticator's
// authenticated branch, so it never fires for anonymous visitors and can't
// loop the Authenticator's own state machine -- it renders once and flips one
// boolean.
//
// The wait is latency-neutral: the very first GraphQL call was going to pay
// for this same session fetch anyway; doing it up front just means one
// Cognito Identity round-trip instead of several concurrent throttled ones.
// It also only runs on entry into /me/* -- this component stays mounted
// across in-app navigation, so pages don't re-gate as the user moves around.
//
// A FAILED warm still renders the pages: the rejected promise was dropped
// from the cache, so each page fetch retries the session itself and surfaces
// its own real error. Gating on success would turn a transient identity-pool
// blip into a blank app.
const WarmedOutlet = () => {
  const [warmed, setWarmed] = useState(false);
  const [showNotice, setShowNotice] = useState(false);

  useEffect(() => {
    let mounted = true;
    const noticeTimer = setTimeout(() => {
      if (mounted) setShowNotice(true);
    }, WARMING_NOTICE_DELAY_MS);

    warmAuthSession()
      .catch(() => {})
      .finally(() => {
        if (!mounted) return;
        clearTimeout(noticeTimer);
        setWarmed(true);
      });

    return () => {
      mounted = false;
      clearTimeout(noticeTimer);
    };
  }, []);

  if (warmed) return <Outlet />;
  return showNotice ? <WarmingNotice /> : null;
};

export default WarmedOutlet;
